import { Sequence, useCurrentFrame } from 'remotion';
import { ElementType } from '@goldcast/player-config/src/types/Element/Type';
import { CompositionConfig } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/CompositionConfig';
import { Element } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/Element';
import { DEFAULT_PREMOUNT_DURATION } from '@goldcast/player-config/src/constants/Remotion.constants';
import AudioElementComponent from '../AudioElementComponent/AudioElementComponent';
import ImageElementComponent from '../ElementComponent/ImageElementComponent/ImageElementComponent';
import TextElementComponent from '../ElementComponent/TextElementComponent/TextElementComponent';
import VideoElementComponent from '../VideoElementComponent/VideoElementComponent';
import LottieElementComponent from '../ElementComponent/LottieElementComponent/LottieElementComponent';
import HidePreMountTrack from './HidePreMountTrack';

const ElementsTrackComponent: React.FC<{
  elements: Element[];
  source: 'SEGMENT' | 'TRACK';
  canEditSpeakerLabel: boolean;
  canEditImages: boolean;
  canEditTextElements: boolean;
  canEditVideos: boolean;
  canEditLottie?: boolean;
  onOutlineRelease: CompositionConfig['onOutlineRelease'];
}> = ({
  elements,
  source,
  canEditSpeakerLabel,
  canEditImages,
  canEditTextElements,
  canEditVideos,
  canEditLottie = false,
  onOutlineRelease
}) => {
  const currentFrame = useCurrentFrame();
  return elements.map(element => {
    if (!element) {
      return null;
    }

    switch (element.type) {
      case ElementType.TEXT:
        return (
          <TextElementComponent
            key={source + '-' + element.id}
            element={element}
            onOutlineRelease={onOutlineRelease}
            canEditText={canEditTextElements}
          />
        );
      // case ElementType.SHAPE:
      //   return <ShapeElementComponent key={source + '-' + element.id} element={element} />;
      case ElementType.IMAGE: {
        const durationInFrames = element.toFrame - element.fromFrame;

        return (
          <Sequence
            key={source + '-' + element.id}
            from={element.fromFrame}
            durationInFrames={durationInFrames > 0 ? durationInFrames : 0}
            premountFor={DEFAULT_PREMOUNT_DURATION}
          >
            <HidePreMountTrack isPreMounted={element.fromFrame > currentFrame}>
              <ImageElementComponent
                canEditImages={canEditImages}
                element={element}
                onOutlineRelease={onOutlineRelease}
              />
            </HidePreMountTrack>
          </Sequence>
        );
      }
      case ElementType.AUDIO:
        return (
          <Sequence
            key={source + '-' + element.id}
            from={element.fromFrame}
            durationInFrames={element.toFrame - element.fromFrame}
            premountFor={DEFAULT_PREMOUNT_DURATION}
          >
            <AudioElementComponent element={element} />
          </Sequence>
        );
      case ElementType.VIDEO:
        return (
          <Sequence
            key={source + '-' + element.id}
            from={element.fromFrame}
            durationInFrames={element.toFrame - element.fromFrame}
            premountFor={DEFAULT_PREMOUNT_DURATION}
          >
            <HidePreMountTrack isPreMounted={element.fromFrame > currentFrame}>
              <VideoElementComponent
                element={{ ...element, fromFrame: 0, toFrame: element.toFrame - element.fromFrame }}
                canEditSpeakerLabel={canEditSpeakerLabel}
                canEditVideos={canEditVideos}
                onOutlineRelease={onOutlineRelease}
              />
            </HidePreMountTrack>
          </Sequence>
        );
      case ElementType.LOTTIE:
        return (
          <Sequence
            key={source + '-' + element.id}
            from={element.fromFrame}
            durationInFrames={element.toFrame - element.fromFrame}
            premountFor={DEFAULT_PREMOUNT_DURATION}
          >
            <LottieElementComponent
              element={element}
              canEditLottie={canEditLottie}
              onOutlineRelease={onOutlineRelease}
            />
          </Sequence>
        );
      default:
        return null;
    }
  });
};

export default ElementsTrackComponent;
